import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { LoadingState } from '@/components/loading-state'
import { useCategories } from '@/features/discovery/hooks/use-categories'
import {
  MAX_DISTANCE_KM,
  MIN_DISTANCE_KM,
} from '@/features/discovery/filters/filters'
import { localizedCategoryName } from '@/lib/category-labels'

import type { ProximitySettingsState } from '../hooks/use-proximity-settings'
import { DEFAULT_PROXIMITY_SETTINGS } from '../hooks/use-proximity-settings'
import { NotificationNotice } from './notice'

interface ProximityCardProps {
  state: ProximitySettingsState
}

/**
 * "New activities near you" (D-074, D-075): a master switch, the same radius
 * slider bounds as the discovery filters, the per-activity-category opt-in
 * (every category by default; `null` on the server means all) and the
 * notifications delay in minutes ("Intervallo notifiche") driving the
 * debounce between two nearby-activity notifications. The stored device
 * position is used first; the profile city center is the fallback; disabling
 * deletes the stored position and the pending queue.
 */
export function ProximityCard({ state }: ProximityCardProps) {
  const { t } = useTranslation()
  const headingId = useId()
  const radiusId = useId()
  const delayId = useId()
  const { settings, phase, saving, saveErrorKey, retry, update } = state
  const values = settings ?? DEFAULT_PROXIMITY_SETTINGS
  const categories = useCategories()
  // `null` means every category; explicit lists are the opt-in set.
  const selected = new Set(values.selectedCategories ?? [])
  const allSelected = values.selectedCategories === null
  // The slider and the delay input save on release/blur, not per frame.
  const [radiusDraft, setRadiusDraft] = useState<number | null>(null)
  const [delayDraft, setDelayDraft] = useState<number | null>(null)

  function commitRadius() {
    if (radiusDraft === null) return
    const next = radiusDraft
    setRadiusDraft(null)
    if (next !== values.radiusKm) void update({ radiusKm: next })
  }

  function commitDelay() {
    if (delayDraft === null) return
    const next = delayDraft
    setDelayDraft(null)
    if (next !== values.debounceMinutes) {
      void update({ debounceMinutes: next })
    }
  }

  function toggleCategory(slug: string) {
    const next = new Set(allSelected ? categories.categories.map((c) => c.slug) : selected)
    if (next.has(slug)) {
      next.delete(slug)
    } else {
      next.add(slug)
    }
    void update({ selectedCategories: [...next] })
  }

  return (
    <section aria-labelledby={headingId} className="mt-4">
      <h2 id={headingId} className="text-lg">
        {t('notifications.proximity.title')}
      </h2>
      <p className="mt-1 max-w-[60ch] text-sm leading-relaxed text-muted">
        {t('notifications.proximity.description')}
      </p>

      {saveErrorKey ? (
        <div className="mt-3">
          <NotificationNotice tone="error" messageKey={saveErrorKey} />
        </div>
      ) : null}

      {phase === 'loading' ? (
        <LoadingState labelKey="notifications.proximity.loading" compact />
      ) : phase === 'error' ? (
        <div className="mt-3">
          <NotificationNotice
            tone="error"
            messageKey="notifications.proximity.loadError"
            onRetry={retry}
          />
        </div>
      ) : (
        <div className="mt-3 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <span id={`${delayId}-master`} className="text-sm font-bold">
              {t('notifications.proximity.enable')}
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={values.enabled}
              aria-labelledby={`${delayId}-master`}
              aria-busy={saving}
              className="preference-switch"
              onClick={() => void update({ enabled: !values.enabled })}
            >
              <span className="preference-switch__thumb" aria-hidden="true" />
            </button>
          </div>

          {values.enabled ? (
            <>
              <div>
                <label htmlFor={radiusId} className="field-label">
                  {t('notifications.proximity.radius', {
                    distance: radiusDraft ?? values.radiusKm,
                  })}
                </label>
                <input
                  id={radiusId}
                  type="range"
                  min={MIN_DISTANCE_KM}
                  max={MAX_DISTANCE_KM}
                  step={1}
                  value={radiusDraft ?? values.radiusKm}
                  disabled={saving}
                  onChange={(event) =>
                    setRadiusDraft(Number(event.target.value))
                  }
                  onPointerUp={commitRadius}
                  onKeyUp={commitRadius}
                  onBlur={commitRadius}
                />
                <p className="mt-1 text-xs text-muted">
                  {t('notifications.proximity.radiusHint')}
                </p>
              </div>

              <div>
                <label htmlFor={delayId} className="field-label">
                  {t('notifications.proximity.delay')}
                </label>
                <input
                  id={delayId}
                  className="field-input"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={1440}
                  step={1}
                  value={delayDraft ?? values.debounceMinutes}
                  disabled={saving}
                  onChange={(event) => setDelayDraft(Number(event.target.value))}
                  onBlur={commitDelay}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.currentTarget.blur()
                    }
                  }}
                />
                <p className="mt-1 text-xs text-muted">
                  {t('notifications.proximity.delayHint')}
                </p>
              </div>

              <div>
                <p className="field-label">
                  {t('notifications.proximity.categories')}
                </p>
                {categories.phase === 'error' || categories.phase === 'loading' ? (
                  <p className="text-sm text-muted">
                    {t('notifications.proximity.categoriesUnavailable')}
                  </p>
                ) : (
                  <ul className="mt-1 flex flex-wrap gap-2">
                    {categories.categories.map((category) => {
                      const active = allSelected || selected.has(category.slug)
                      return (
                        <li key={category.slug}>
                          <button
                            type="button"
                            className={`filter-chip ${active ? 'is-active' : ''}`}
                            aria-pressed={active}
                            aria-busy={saving}
                            disabled={saving}
                            onClick={() => toggleCategory(category.slug)}
                          >
                            {localizedCategoryName(
                              t,
                              category.slug,
                              category.name,
                            )}
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                )}
                <p className="mt-1 text-xs text-muted">
                  {t('notifications.proximity.categoriesHint')}
                </p>
              </div>

              <p className="text-xs text-muted">
                {values.position
                  ? t('notifications.proximity.positionKnown')
                  : t('notifications.proximity.positionUnknown')}
              </p>
            </>
          ) : null}
        </div>
      )}
    </section>
  )
}
